// Pure helpers behind the record view's identity line (design spec §6.3:
// title · where it lives · kind · revision shown).

import {
  getFileCategory,
  type FileCategory,
} from "src/views/assistant/projects/helpers/fileCategory";
import {
  FAILURE_STATUS,
  INDEXED_STATUSES,
} from "src/views/assistant/projects/helpers/processingStatus";
import {
  isCurrentRevision,
  revisionDisplayLabel,
  revisionLabelText,
} from "src/api/records";
import type { RecordInfo, RevisionInfo } from "src/api/records";

const KIND_LABELS: Record<FileCategory, string> = {
  image: "Image",
  pdf: "PDF document",
  word: "Word document",
  slides: "Presentation",
  spreadsheet: "Spreadsheet",
  csv: "CSV table",
  tsv: "TSV table",
  text: "Text document",
  schedule: "Schedule",
  archive: "Archive",
  mail: "Email",
  other: "Document",
};

export const recordKindLabel = (fileName: string | null | undefined): string =>
  fileName ? KIND_LABELS[getFileCategory(fileName)] : "Document";

// Defined with the wire shape it reads so the record service can use it too
// without importing back through this module's barrel.
export { recordTitle } from "src/api/records/types";

/** "page_count" → "Page Count": underscores become spaces, each word's first letter upper-cased. */
export const titleCaseFieldLabel = (key: string): string =>
  key
    .replace(/_/g, " ")
    .split(" ")
    .filter((word) => word !== "")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

/** "Aug 2026"-style label, or null when the date is missing/unparsable. */
export const monthYearLabel = (
  value: string | null | undefined,
): string | null => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
};

/** Page count from the extractor (extracted_metadata.page_count), or null. */
export const recordPageCount = (
  sdrInfo: RecordInfo | null | undefined,
): number | null => {
  const raw = (
    sdrInfo?.data?.extracted_metadata as { page_count?: unknown } | undefined
  )?.page_count;
  const n =
    typeof raw === "number"
      ? raw
      : typeof raw === "string" && raw.trim() !== ""
        ? Number(raw)
        : NaN;
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : null;
};

export type RecordStatusChip = {
  label: "Indexed" | "Processing" | "Failed";
  color: "success" | "warning" | "error";
};

/**
 * The one status→chip mapping for a record, shared with ProjectFileViewer's
 * indexBadge so the same record cannot report two different words depending on
 * which surface opened it. Callers that know states `processing_status` does
 * not — no record yet, a reprocess in flight, an image that is never indexed —
 * handle those before calling.
 *
 * A record with an SDR row but no status is legacy ingest, which is indexed.
 */
export const recordStatusChip = (
  processingStatus: string | null | undefined,
): RecordStatusChip => {
  const status = (processingStatus ?? "").toLowerCase();
  if (!status) return { label: "Indexed", color: "success" };
  if (INDEXED_STATUSES.has(status))
    return { label: "Indexed", color: "success" };
  if (FAILURE_STATUS.test(status)) return { label: "Failed", color: "error" };
  return { label: "Processing", color: "warning" };
};

/**
 * The identity line's revision segment: the current revision is marked
 * "(Current)"; a superseded one shows the held revision's name instead.
 */
export const shownRevisionLabel = (
  rev: RevisionInfo | null | undefined,
): string | null => {
  if (!rev) return null;
  if (isCurrentRevision(rev)) {
    return rev.revision_label
      ? `${revisionLabelText(rev.revision_label)} (Current)`
      : "(Current)";
  }
  return revisionDisplayLabel(rev);
};
